﻿/* eslint-disable react-refresh/only-export-components -- INDUSTRY_KEYS is derived from the picker's option list and stays co-located */
import { useTranslation } from "react-i18next";
import {
  Store,
  Coffee,
  Scissors,
  Stethoscope,
  GraduationCap,
  Briefcase,
  Wrench,
  Megaphone,
  MoreHorizontal,
  type LucideIcon,
} from "lucide-react";

type IndustryOption = {
  key: string;
  icon: LucideIcon;
  color: string;
  gradient: string;
};

const OPTIONS: IndustryOption[] = [
  {
    key: "retail",
    icon: Store,
    color: "text-emerald-400",
    gradient: "from-emerald-500/20 via-emerald-500/5 to-transparent",
  },
  {
    key: "food_beverage",
    icon: Coffee,
    color: "text-rose-400",
    gradient: "from-rose-500/20 via-rose-500/5 to-transparent",
  },
  {
    key: "services",
    icon: Scissors,
    color: "text-violet-400",
    gradient: "from-violet-500/20 via-violet-500/5 to-transparent",
  },
  {
    key: "healthcare",
    icon: Stethoscope,
    color: "text-sky-400",
    gradient: "from-sky-500/20 via-sky-500/5 to-transparent",
  },
  {
    key: "education",
    icon: GraduationCap,
    color: "text-amber-400",
    gradient: "from-amber-500/20 via-amber-500/5 to-transparent",
  },
  {
    key: "consulting",
    icon: Briefcase,
    color: "text-indigo-400",
    gradient: "from-indigo-500/20 via-indigo-500/5 to-transparent",
  },
  {
    key: "construction",
    icon: Wrench,
    color: "text-orange-400",
    gradient: "from-orange-500/20 via-orange-500/5 to-transparent",
  },
  {
    key: "marketing",
    icon: Megaphone,
    color: "text-pink-400",
    gradient: "from-pink-500/20 via-pink-500/5 to-transparent",
  },
  {
    key: "other",
    icon: MoreHorizontal,
    color: "text-slate-400",
    gradient: "from-slate-500/20 via-slate-500/5 to-transparent",
  },
];

type Props = {
  value: string;
  onChange: (key: string) => void;
};

export function IndustryPicker({ value, onChange }: Props) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
      {OPTIONS.map(({ key, icon: Icon, color, gradient }) => {
        const selected = value === key;
        return (
          <button
            type="button"
            key={key}
            onClick={() => onChange(key)}
            aria-pressed={selected}
            className={`group relative overflow-hidden rounded-xl border text-left px-3.5 py-3 transition-all ${
              selected
                ? "border-blue-500 bg-gradient-to-br from-blue-500/15 to-blue-500/5 ring-2 ring-blue-500/40 shadow-lg shadow-blue-500/10"
                : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60 hover:border-slate-300 dark:hover:border-slate-700 hover:-translate-y-0.5"
            }`}
          >
            <div
              className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none`}
            />
            <div className="relative flex items-start gap-2.5">
              <span
                className={`flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-slate-50 dark:bg-slate-800/70 ${color}`}
              >
                <Icon size={18} />
              </span>
              <div className="min-w-0">
                <p className="font-semibold text-sm text-fg-primary leading-tight">
                  {t(`onboarding.industries.${key}.label`)}
                </p>
                <p className="text-[11px] text-fg-secondary mt-0.5 leading-snug line-clamp-2">
                  {t(`onboarding.industries.${key}.examples`)}
                </p>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

export const INDUSTRY_KEYS = OPTIONS.map((o) => o.key);
